import React, { useState, useEffect } from 'react';
import { CalendarBot } from '../services/calendarWebSocketService';

interface BotSelectorProps {
  bots: CalendarBot[];
  selectedBotId: string | null;
  connectingBotId: string | null; // Bot currently being connected to (loading state)
  onSelectBot: (botId: string, websocketUrl?: string, botSessionId?: string, isSalespersonHost?: boolean) => void;
  isCalendarConnected: boolean;
  onBotTerminated?: () => void; // Callback when bot is successfully terminated
}

export const BotSelector: React.FC<BotSelectorProps> = ({
  bots,
  selectedBotId,
  connectingBotId,
  onSelectBot,
  isCalendarConnected
}) => {
  // Local state for dropdown value (allows selecting scheduled bots for status view)
  const [dropdownValue, setDropdownValue] = useState<string>('');

  // Sync dropdown with parent's selectedBotId when it changes or when bot appears in list
  useEffect(() => {
    if (selectedBotId) {
      // Check if the selected bot exists in the list
      const activeBots = bots.filter(bot => bot.status !== 'terminated');
      const botExists = activeBots.some(
        bot => bot.botId === selectedBotId || bot.botSessionId === selectedBotId
      );

      // Only set dropdown value if the bot exists in the list
      if (botExists) {
        setDropdownValue(selectedBotId);
      }
    }
  }, [selectedBotId, bots]);

  // Reset dropdown if selected bot is no longer in the list (e.g., terminated)
  useEffect(() => {
    if (dropdownValue && !selectedBotId) {
      // Only reset if parent hasn't selected anything (avoid clearing during bot addition)
      const activeBots = bots.filter(bot => bot.status !== 'terminated');
      const stillExists = activeBots.some(
        bot => bot.botId === dropdownValue || bot.botSessionId === dropdownValue
      );
      if (!stillExists) {
        setDropdownValue('');
      }
    }
  }, [bots, dropdownValue, selectedBotId]);

  if (!isCalendarConnected) {
    return null;
  }

  // Count only non-terminated bots for display
  const activeBots = bots.filter(bot => bot.status !== 'terminated');

  const getPlatformIcon = (platform: string) => {
    switch (platform) {
      case 'meet':
        return '🎥';
      case 'zoom':
        return '💼';
      case 'teams':
        return '👥';
      default:
        return '📞';
    }
  };

  const getStatusInfo = (status: string) => {
    switch (status) {
      case 'active':
        return { label: 'Active', className: 'bg-green-100 text-green-700', icon: '✓' };
      case 'creating':
        return { label: 'Creating...', className: 'bg-yellow-100 text-yellow-700', icon: '⏳' };
      case 'joining':
        return { label: 'In Lobby', className: 'bg-orange-100 text-orange-700', icon: '🚪' };
      case 'scheduled':
        return { label: 'Scheduled', className: 'bg-blue-100 text-blue-700', icon: '📅' };
      default:
        return { label: status, className: 'bg-gray-100 text-gray-700', icon: '•' };
    }
  };

  const formatTime = (isoString: string) => {
    // If timestamp doesn't have timezone info, treat it as UTC by appending 'Z'
    let timestampWithTz = isoString;
    if (!isoString.endsWith('Z') && !isoString.includes('+') && !isoString.includes('-', 10)) {
      timestampWithTz = isoString + 'Z';
    }

    const date = new Date(timestampWithTz);
    // Convert UTC to local time and format
    const formatted = date.toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true  // Use 12-hour format with AM/PM
    });
    return formatted;
  };

  const handleDropdownChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    setDropdownValue(selectedValue);

    if (!selectedValue) return;

    // Find bot by botId or botSessionId (for scheduled bots without botId)
    const bot = activeBots.find(b => b.botId === selectedValue || b.botSessionId === selectedValue);
    if (bot && bot.botId) {
      // Only call onSelectBot if bot has a botId (active/joining bots can connect)
      onSelectBot(bot.botId, bot.websocketUrl, bot.botSessionId, bot.isSalespersonHost);
    }
    // For scheduled bots without botId, just show the status but don't connect
  };

  // Get the currently selected bot for status display based on dropdown value
  const selectedBot = dropdownValue
    ? activeBots.find(bot => bot.botId === dropdownValue || bot.botSessionId === dropdownValue)
    : null;
  const isConnecting = dropdownValue && connectingBotId === dropdownValue;

  // No active bots - show empty state
  if (activeBots.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
        <div className="flex items-center space-x-2">
          <span className="text-gray-400">📅</span>
          <span className="text-xs text-gray-500">No Active Bots</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
      {/* Dropdown */}
      <div className="flex items-center space-x-2">
        <span className="text-blue-600">📅</span>
        <select
          value={dropdownValue}
          onChange={handleDropdownChange}
          disabled={!!isConnecting}
          className={`
            flex-1 text-sm px-2 py-1 border border-blue-300 rounded
            focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            bg-white
            ${isConnecting ? 'cursor-wait opacity-70' : ''}
          `}
        >
          <option value="">Select a meeting...</option>
          {activeBots.map((bot) => (
            <option key={bot.botSessionId} value={bot.botId || bot.botSessionId}>
              {getPlatformIcon(bot.platform)} {bot.eventTitle} ({formatTime(bot.startTime)})
            </option>
          ))}
        </select>
      </div>

      {/* Status display below dropdown when a bot is selected */}
      {dropdownValue && selectedBot && (
        <div className="mt-2 pt-2 border-t border-blue-200">
          {isConnecting ? (
            <div className="flex items-center space-x-2 text-xs text-purple-700">
              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Connecting to meeting...</span>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2 text-xs text-gray-600">
                <span>{getPlatformIcon(selectedBot.platform)}</span>
                <span>🕐 {formatTime(selectedBot.startTime)}</span>
              </div>
              <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusInfo(selectedBot.status).className}`}>
                {getStatusInfo(selectedBot.status).icon} {getStatusInfo(selectedBot.status).label}
              </span>
            </div>
          )}

          {/* Additional status messages */}
          {selectedBot.status === 'scheduled' && (
            <div className="mt-1 text-xs text-blue-600">
              Bot will join automatically before meeting starts
            </div>
          )}
          {selectedBot.status === 'joining' && !isConnecting && (
            <div className="mt-1 text-xs text-orange-600">
              Waiting to be admitted to meeting
            </div>
          )}
          {selectedBot.status === 'creating' && (
            <div className="mt-1 text-xs text-yellow-600">
              Creating bot instance...
            </div>
          )}
          {selectedBot.status === 'active' && !isConnecting && (
            <div className="mt-1 text-xs text-green-600">
              Currently monitoring this meeting
            </div>
          )}
        </div>
      )}
    </div>
  );
};
